import { useState } from "react";
import { Mic, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";

const MAX_LEN = 1000;
const EMPTY = {
  name: "",
  location: "",
  email: "",
  question: "",
  opt_in: false,
  website: "", // honeypot — never rendered visibly
};

/**
 * Submission form for fan questions to the DemoPod podcast.
 * - 1000-char question limit with live counter
 * - Honeypot field for bot filtering
 * - Optional opt-in adds the email to the same subscriber list as the email lightbox
 */
export default function FanQuestionForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const remaining = MAX_LEN - form.question.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.submitFanQuestion({
        name: form.name.trim(),
        location: form.location.trim(),
        email: form.email.trim(),
        question: form.question.trim(),
        opt_in: form.opt_in,
        website: form.website,
      });
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      logger.warn("Fan question submit failed:", err.message);
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setError(detail || "Too many submissions. Please try again later.");
      } else {
        setError(detail || "Could not send your question right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-20 px-6 md:px-12 bg-demolition-paper border-t border-white/5"
      data-testid="fan-question-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <Mic className="w-12 h-12 text-demolition-red mx-auto mb-4" />
          <h2 className="font-russo text-3xl sm:text-4xl text-white uppercase tracking-tighter mb-3">
            SUBMIT A QUESTION FOR DEMOPOD
          </h2>
          <p className="font-mono text-white/60 text-sm max-w-xl mx-auto">
            Got a question for Ax & Smash? Submit below and yours may be read and answered on a future episode of the podcast.
          </p>
        </div>

        {submitted ? (
          <div
            className="bg-demolition-subtle border border-demolition-red/40 p-8 text-center"
            data-testid="fan-question-success"
          >
            <CheckCircle2 className="w-14 h-14 text-demolition-red mx-auto mb-4" />
            <h3 className="font-russo text-2xl text-white uppercase tracking-tighter mb-2">
              QUESTION SUBMITTED
            </h3>
            <p className="font-mono text-white/60 text-sm mb-6">
              Thanks — keep an eye on the show. You might just hear your name.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="px-6 py-2 border border-demolition-red text-demolition-red font-teko text-base uppercase tracking-widest hover:bg-demolition-red hover:text-white transition-colors"
              data-testid="fan-question-submit-another"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-demolition-subtle border border-white/10 p-6 sm:p-8 space-y-5"
            data-testid="fan-question-form"
          >
            {/* Honeypot — visually hidden, off-screen, tabindex -1, autocomplete off */}
            <div className="absolute -left-[10000px] w-px h-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update("website")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-teko text-sm text-white/60 uppercase tracking-wider block mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="w-full px-4 py-3 bg-demolition-paper border border-white/10 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors"
                  data-testid="fan-question-name"
                />
              </div>
              <div>
                <label className="font-teko text-sm text-white/60 uppercase tracking-wider block mb-1">
                  Location *
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={form.location}
                  onChange={update("location")}
                  placeholder="Philadelphia, PA"
                  className="w-full px-4 py-3 bg-demolition-paper border border-white/10 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors"
                  data-testid="fan-question-location"
                />
              </div>
            </div>

            <div>
              <label className="font-teko text-sm text-white/60 uppercase tracking-wider block mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-demolition-paper border border-white/10 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors"
                data-testid="fan-question-email"
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-1">
                <label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                  Your Question *
                </label>
                <span
                  className={`font-mono text-xs ${remaining < 50 ? "text-demolition-red" : "text-white/30"}`}
                  data-testid="fan-question-charcount"
                >
                  {remaining} characters left
                </span>
              </div>
              <textarea
                required
                maxLength={MAX_LEN}
                rows={6}
                value={form.question}
                onChange={update("question")}
                placeholder="Ask Ax & Smash anything..."
                className="w-full px-4 py-3 bg-demolition-paper border border-white/10 text-white font-mono placeholder:text-white/30 focus:outline-none focus:border-demolition-red transition-colors resize-none"
                data-testid="fan-question-text"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="fan-question-optin-label">
              <input
                type="checkbox"
                checked={form.opt_in}
                onChange={update("opt_in")}
                className="mt-1 w-4 h-4 accent-demolition-red flex-shrink-0"
                data-testid="fan-question-optin"
              />
              <span className="font-mono text-white/70 text-sm leading-relaxed group-hover:text-white transition-colors">
                Subscribe me to the Ax &amp; Smash email list for updates on new episodes and merch.
              </span>
            </label>

            {error && (
              <p className="font-mono text-demolition-red text-xs" data-testid="fan-question-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-8 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-widest hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
              data-testid="fan-question-submit"
            >
              {submitting ? "SUBMITTING..." : "SUBMIT QUESTION"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
